import React, { useEffect } from 'react';
import { track } from '@amplitude/analytics-browser';
import { Alert, Button, Spinner } from '@nimbus-ds/components';
import {
  useIdentityValidation,
  useReinitializeRpgAccountMutation,
  useStoreInfo,
  useToast,
} from 'commons/hooks';
import { useTranslationWithPrefix } from 'commons/utils';
import { PendingTrialPeriodAlertPage } from '../PendingTrialPeriodAlert';

interface InterfaceCanceledAlert {
  page: PendingTrialPeriodAlertPage;
}

const CanceledAlert = ({ page }: InterfaceCanceledAlert): JSX.Element => {
  const { t } = useTranslationWithPrefix('commons.canceledAlert');
  const identityValidation = useIdentityValidation();
  const { storeInfo } = useStoreInfo();
  const { showToastSuccess, showToastError } = useToast();

  const { mutate, isPending: isMutating } = useReinitializeRpgAccountMutation({
    onSuccess: () => {
      showToastSuccess(t('successMessage'));
      identityValidation.refetch();
    },
    onError: () => {
      showToastError(t('error'));
    },
  });

  useEffect(() => {
    track('pago-nube-cuenta-inactiva-view', {
      page,
    });
  }, [page]);

  const handleButtonClick = () => {
    track('pago-nube-cuenta-inactiva-click', {
      page,
    });
    mutate({
      fiscalDocument: storeInfo.owner.document,
    });
  };

  const isButtonLoading = isMutating || identityValidation.isRefetching;

  return (
    <Alert
      data-testid="canceled-alert"
      title={t('title')}
      show
      appearance="warning"
    >
      {t('description')}
      <Button
        appearance="primary"
        onClick={handleButtonClick}
        disabled={isButtonLoading}
      >
        {isButtonLoading && <Spinner size="small" />}
        {t('button')}
      </Button>
    </Alert>
  );
};

export default CanceledAlert;
